import {computed, type Ref} from "vue";
import type {
  ComponentType,
  ComponentGroup,
  ServiceCategory,
  ServiceComponent,
  ConnectionComponent,
  DatastoreComponent,
} from "@/types/component-types";
import type {OrgComponents} from "@/queries/components";

interface Filters {
  searchQuery: Ref<string>;
  selectedTypes: Ref<ComponentType[]>;
  selectedCategories: Ref<string[]>;
  selectedProducts: Ref<string[]>;
}

type AnyComponent = ServiceComponent | ConnectionComponent | DatastoreComponent;

const CATEGORY_ICONS: Record<string, string> = {
  Communication: "bi-chat",
  "Data & Storage": "bi-database",
  "Logic & Flow": "bi-diagram-3",
  Integrations: "bi-puzzle",
  Analytics: "bi-graph-up",
  Security: "bi-shield-check",
  Monitoring: "bi-activity",
  "E-commerce": "bi-cart",
  Marketing: "bi-megaphone",
  Financial: "bi-currency-dollar",
};

const getCategoryIcon = (category: string) => CATEGORY_ICONS[category] ?? "bi-box";

export function useComponentLibrary(data: Ref<OrgComponents>, filters: Filters) {
  const allComponents = computed<AnyComponent[]>(() => [
    ...data.value.services,
    ...data.value.connections,
    ...data.value.datastores,
  ]);

  const accessibleComponents = computed<AnyComponent[]>(() => {
    const subs = data.value.subscriptions;
    return allComponents.value.filter((c) => {
      if (c.isGeneric || c.source === "system") return true;
      if (!c.requiresSubscription) return true;

      const sub = subs.find(
        (s) => s.productId === c.productId && (s.status === "active" || s.status === "pending")
      );
      if (!sub) return false;
      return sub.permissions.some((p) => p.componentId === c.id);
    });
  });

  const filteredComponents = computed<AnyComponent[]>(() => {
    let result = accessibleComponents.value;
    const q = filters.searchQuery.value.toLowerCase();
    if (q) {
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.description.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      );
    }
    if (filters.selectedTypes.value.length > 0) {
      result = result.filter((c) => filters.selectedTypes.value.includes(c.type));
    }
    if (filters.selectedCategories.value.length > 0) {
      result = result.filter((c) => filters.selectedCategories.value.includes(c.category));
    }
    if (filters.selectedProducts.value.length > 0) {
      result = result.filter(
        (c) => c.productId && filters.selectedProducts.value.includes(c.productId)
      );
    }
    return result;
  });

  const componentGroups = computed<ComponentGroup[]>(() => {
    const groups = new Map<string, ComponentGroup>();
    for (const c of accessibleComponents.value) {
      const key = c.source === "system" ? "system" : c.productId!;
      if (!groups.has(key)) {
        const sub = data.value.subscriptions.find((s) => s.productId === c.productId);
        groups.set(key, {
          source: c.source,
          productId: c.productId,
          productName: c.productName,
          components: [],
          hasSubscription: !!sub,
          subscriptionStatus: sub?.status,
        });
      }
      groups.get(key)!.components.push(c);
    }
    return Array.from(groups.values()).sort((a, b) => {
      if (a.source === "system") return 1;
      if (b.source === "system") return -1;
      if (a.hasSubscription && !b.hasSubscription) return -1;
      if (!a.hasSubscription && b.hasSubscription) return 1;
      return (a.productName || "").localeCompare(b.productName || "");
    });
  });

  const serviceCategories = computed<ServiceCategory[]>(() => {
    const map = new Map<string, ServiceCategory>();
    for (const c of filteredComponents.value) {
      const key = `${c.category}-${c.source}-${c.productId || "system"}`;
      if (!map.has(key)) {
        map.set(key, {
          id: key,
          name: c.category,
          description: `${c.category} components`,
          icon: getCategoryIcon(c.category),
          source: c.source,
          productId: c.productId,
          productName: c.productName,
          components: [],
          isExpanded: true,
          hasSubscription:
            c.source === "system" ||
            data.value.subscriptions.some(
              (s) => s.productId === c.productId && s.status === "active"
            ),
        });
      }
      map.get(key)!.components.push({
        id: c.id,
        name: c.name,
        description: c.description,
        icon: c.icon,
        type: c.type,
        source: c.source,
        productId: c.productId,
        productName: c.productName,
        isAvailable: true,
        requiresSubscription: c.requiresSubscription,
        usageCount: c.usageMetrics?.monthlyUses,
        lastUsed: c.usageMetrics?.lastUsed,
      });
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.source === "system" && b.source !== "system") return -1;
      if (a.source !== "system" && b.source === "system") return 1;
      const pa = a.productName || "";
      const pb = b.productName || "";
      if (pa !== pb) return pa.localeCompare(pb);
      return a.name.localeCompare(b.name);
    });
  });

  const componentStats = computed(() => ({
    total: allComponents.value.length,
    accessible: accessibleComponents.value.length,
    byType: {
      service: data.value.services.length,
      connection: data.value.connections.length,
      datastore: data.value.datastores.length,
    },
    subscriptionCount: data.value.subscriptions.filter((s) => s.status === "active").length,
  }));

  return {
    allComponents,
    accessibleComponents,
    filteredComponents,
    componentGroups,
    serviceCategories,
    componentStats,
  };
}
